import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Select } from '~/components/form';
import { type PageAction, RecordPageShell } from '~/components/layout';
import TemplateForm from '~/components/pages/notifications/templates/TemplateForm';
import TemplateStatusChip from '~/components/pages/notifications/templates/TemplateStatusChip';
import { Alert, ConfirmModal } from '~/components/ui';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { useUrlParams } from '~/hooks/useUrlParams';
import { apiErrorMessage, errorText, isNotFound } from '~/lib/apiError';
import {
  type TemplateFormValues,
  templateStatus,
  templateToForm,
  toTemplateUpdate,
} from '~/lib/notification/templateForm';
import {
  useDeactivateTemplateMutation,
  useGetTemplateQuery,
  usePublishTemplateMutation,
  useUpdateTemplateMutation,
} from '~/modules/query/notification/templates';
import type { Template } from '~/modules/query/notification/types';
import { routes } from '~/app/routes';

type Confirm = 'publish' | 'deactivate' | null;

/** A positive whole number from `?version=`, else undefined (the latest version). */
const parseVersion = (raw: string | null): number | undefined => {
  const n = Number(raw);
  return raw && Number.isInteger(n) && n > 0 ? n : undefined;
};

export default function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const url = useUrlParams();
  const requested = parseVersion(url.get('version'));

  // The status comes from the latest record; a `?version=` record's version is only the one being viewed.
  const latestQuery = useGetTemplateQuery({ id: id ?? '' }, { skip: !id });
  const versionQuery = useGetTemplateQuery(
    { id: id ?? '', version: requested },
    { skip: !id || requested === undefined },
  );
  const latest = latestQuery.currentData;
  const viewedQuery = requested === undefined ? latestQuery : versionQuery;
  const viewed = viewedQuery.currentData;
  const isOldVersion = !!latest && !!viewed && viewed.version !== latest.version;

  const [updateTemplate] = useUpdateTemplateMutation();
  const [publishTemplate] = usePublishTemplateMutation();
  const [deactivateTemplate] = useDeactivateTemplateMutation();

  const { draft, patch, dirty, markSaved } = useRecordDraft(viewed, {
    id: id ?? '',
    version: viewed?.version,
    recordId: (t) => t.id,
    toDraft: templateToForm,
  });
  const guard = useUnsavedGuard(dirty);

  const [confirm, setConfirm] = useState<Confirm>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const save = useRecordSave<TemplateFormValues>({
    save: (form) =>
      updateTemplate({
        id: id ?? '',
        ...toTemplateUpdate(form, templateToForm(viewed as Template)),
      }).unwrap(),
    success: 'Template saved',
    failure: 'The draft could not be saved. Try again.',
  });

  const title = latest?.name || viewed?.templateKey || 'Template';
  const status = latest ? templateStatus(latest) : null;
  // Publishing an old version needs its number; the latest needs none.
  const publishVersion = isOldVersion && viewed?.version ? viewed.version : undefined;
  const canPublish =
    !!latest &&
    (publishVersion === undefined
      ? status?.kind !== 'live'
      : publishVersion !== latest.publishedVersion);

  const publishedLabel = publishVersion ?? latest?.version;

  const publish = async () => {
    if (!id) return;
    setActionError(null);
    try {
      await publishTemplate({ id, templateVersion: publishVersion }).unwrap();
      toast.success(`Version ${publishedLabel} is now live`);
    } catch (error) {
      setActionError(errorText(error, 'The template could not be published. Try again.'));
    } finally {
      setConfirm(null);
    }
  };

  const deactivate = async () => {
    if (!id) return;
    setActionError(null);
    try {
      await deactivateTemplate(id).unwrap();
      toast.success('Template deactivated');
    } catch (error) {
      setActionError(errorText(error, 'The template could not be deactivated. Try again.'));
    } finally {
      setConfirm(null);
    }
  };

  const loadError = viewedQuery.error ?? latestQuery.error;
  const loadFailed = !viewed && !!loadError;
  const versions = latest?.version ? Array.from({ length: latest.version }, (_, i) => i + 1) : [];

  const actions: PageAction[] = [];
  if (latest && status?.kind !== 'notLive') {
    actions.push({
      label: 'Deactivate',
      kind: 'danger',
      onClick: () => setConfirm('deactivate'),
      testId: 'template-deactivate-button',
    });
  }
  if (canPublish) {
    actions.push({
      label: publishVersion === undefined ? 'Publish' : 'Publish this version',
      kind: 'primary',
      onClick: () => setConfirm('publish'),
      testId: 'template-publish-button',
    });
  }

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[
        { label: 'Notifications' },
        { label: 'Templates', href: routes.notificationTemplates },
        { label: title },
      ]}
      actions={actions}
      loading={!draft && !loadError}
      notFound={loadFailed && isNotFound(loadError)}
      notFoundTitle="Template not found"
      backTo={routes.notificationTemplates}
      backLabel="Back to templates"
      error={
        loadFailed && !isNotFound(loadError)
          ? { what: 'the template', message: apiErrorMessage(loadError) }
          : null
      }
      onRetry={() => {
        void latestQuery.refetch();
        if (requested !== undefined) void versionQuery.refetch();
      }}
      guard={guard}
      testId="template"
    >
      {latest && viewed && draft && (
        <>
          {actionError && <Alert variant="error" message={actionError} />}

          <TemplateForm
            templateId={latest.id}
            draft={draft}
            patch={patch}
            isNew={false}
            readOnly={isOldVersion}
            dirty={dirty}
            busy={save.busy}
            error={save.error}
            badge={<TemplateStatusChip latest={latest} testId="template-status" />}
            description={
              isOldVersion
                ? `Version ${viewed.version} is read-only. Switch to the latest version to edit.`
                : 'A message change saves a new draft version, live once published. Name, description and customers apply at once.'
            }
            headerActions={
              <Select
                id="template-version"
                testId="template-version"
                ariaLabel="Version"
                value={String(viewed.version ?? latest.version ?? '')}
                options={versions.map((v) => ({
                  value: String(v),
                  label:
                    v === latest.version
                      ? `Version ${v} (latest)`
                      : v === latest.publishedVersion
                        ? `Version ${v} (live)`
                        : `Version ${v}`,
                }))}
                disabled={dirty}
                onChange={(next) =>
                  url.set({ version: Number(next) === latest.version ? null : next })
                }
              />
            }
            onSubmit={async () => {
              if (await save.submit(draft)) markSaved(draft);
            }}
          />

          <ConfirmModal
            isOpen={confirm === 'publish'}
            title={publishVersion === undefined ? 'Publish template' : 'Publish this version'}
            message={
              `Make version ${publishedLabel} of '${title}' the live version?` +
              (dirty && !isOldVersion ? ' Your unsaved edits are not part of it.' : '')
            }
            confirmLabel="Publish"
            testId="template-publish"
            onClose={() => setConfirm(null)}
            onConfirm={publish}
          />
          <ConfirmModal
            isOpen={confirm === 'deactivate'}
            title="Deactivate template"
            message={`Deactivate '${title}'?`}
            confirmText={title}
            confirmLabel="Deactivate"
            variant="danger"
            dangerNote="Every version stops sending until one is published again."
            testId="template-deactivate"
            onClose={() => setConfirm(null)}
            onConfirm={deactivate}
          />
        </>
      )}
    </RecordPageShell>
  );
}
